import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import posthog from '@/config/analytics/posthog';
import { signOutDevice } from '@/config/auth/deviceToken';
import { logoutMsal } from '@/config/auth/msal';
import { clearSentryUser } from '@/config/monitoring/sentry';
import { queryKeys } from '@/config/query/queryKeys';
import { ROUTES } from '@/utils/routeUtils';

/**
 * Client-side sign-out: revokes the device session, forgets the cached
 * mailbox credential, clears auth caches, and lands on the login page.
 *
 * Deliberately never touches the mailbox connection (the stored Graph
 * credential and its background sync/automations) — that is managed
 * explicitly via /auth/disconnect-mailbox from the Profile page's
 * MailboxConnectionCard.
 */
export function useSignOut() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await signOutDevice();
      await logoutMsal();
      clearSentryUser();
      // Drops the identified distinct id and registered super properties
      // (`user_email`) so a shared machine never stamps the next signed-in user's
      // events with the previous user's identity.
      posthog.reset();
      queryClient.removeQueries({ queryKey: queryKeys.auth.all });
      navigate(ROUTES.LOGIN, { replace: true });
    },
    onError: (error) => {
      console.error('Logout failed:', error);
    },
  });
}
